import axios from 'axios';

// Create axios instance with base configuration
// In production, use relative URLs (same domain) or environment variable
// In development, use localhost
const getBaseURL = () => {
  const envValue = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
  const hasWindow = typeof window !== 'undefined';
  const hostname = hasWindow ? window.location.hostname : '';
  const protocol = hasWindow ? window.location.protocol : '';
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '' ||
    hostname.endsWith('.local');

  // Priority 1: Use explicit env var if provided
  if (envValue !== '') {
    const envIsAbsoluteUrl = envValue.startsWith('http://') || envValue.startsWith('https://');
    const envIsLocalUrl =
      envValue.includes('localhost') ||
      envValue.includes('127.0.0.1') ||
      envValue.includes('://0.0.0.0');

    // If the env value points to localhost but we're not on localhost, ignore it and use relative URL
    if (hasWindow && !isLocalHost && envIsLocalUrl) {
      return '';
    }

    if (envValue.startsWith('/')) {
      const normalized = envValue.replace(/\/+$/, ''); // remove trailing slashes

      // Default value '/api' (or '/') means our endpoints already include '/api'
      if (normalized === '' || normalized === '/api') {
        return '';
      }

      // Allow custom prefixes (e.g., '/backend')
      return normalized.startsWith('/') ? normalized : `/${normalized}`;
    }

    if (envIsAbsoluteUrl) {
      return envValue;
    }

    // Treat as relative path
    return `/${envValue.replace(/^\/+/, '')}`;
  }

  // Priority 2: Runtime detection - for production domains use relative URL
  if (hasWindow && !isLocalHost) {
    return '';
  }

  // Priority 3: If running on localhost but via HTTPS (dev over secure tunnel), still use relative path
  if (hasWindow && isLocalHost && protocol === 'https:') {
    return '';
  }

  // Priority 4: NODE_ENV hints production
  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  // Development fallback - explicit localhost for local dev scenarios
  return 'http://localhost:50102';
};

// Get base URL and log for debugging (remove in production if needed)
const baseURL = getBaseURL();
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', baseURL, 'REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
}

const api = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  withCredentials: false, // No authentication cookies needed for public access
});

const toBase64Url = (value) => {
  const input = String(value || '').trim();
  if (!input) return '';
  if (typeof btoa !== 'function') return input;
  const utf8 = encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (_, p1) =>
    String.fromCharCode(parseInt(p1, 16))
  );
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const buildCompaniesQueryParams = (params = {}) => {
  const queryParams = {};
  if (params.name) queryParams.name = toBase64Url(params.name);
  if (params.asset_id) queryParams.asset_id = toBase64Url(params.asset_id);
  if (params.cursor) queryParams.cursor = params.cursor;
  if (typeof params.limit === 'number') queryParams.limit = params.limit;
  return queryParams;
};

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Request interceptor to add auth headers if needed
api.interceptors.request.use(
  (config) => {
    // Add any auth headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // No authentication redirect needed - public access
    return Promise.reject(error);
  }
);

// Authentication API - simplified (no IDP)
export const authAPI = {
  check: () => api.get('/auth/check'),
  login: () => {
    // No login needed - just redirect to home
    window.location.href = '/';
  },
  logout: async () => {
    // No logout needed - just redirect to home
    window.location.href = '/';
  },
  status: () => api.get('/auth/status'),
};

// ============================================================================
// DATA PROVIDERS API - New Unified Endpoint
// ============================================================================

// Legacy dataProvidersAPI - redirects to descriptors endpoint
export const dataProvidersAPI = {
  /**
   * Query endpoint for all data provider searches
   * @deprecated Use companyLookupAPI.getDescriptors() instead
   * @param {Object} params - Query parameters
   * @param {string} params.company_name - Filter by company name
   * @returns {Promise} Data providers matching the criteria
   */
  query: (params = {}) => {
    // Redirect to canonical companies endpoint
    const queryParams = {};
    if (params.company_name) queryParams.name = params.company_name;
    if (params.name) queryParams.name = params.name;
    if (params.asset_id) queryParams.asset_id = params.asset_id;
    if (params.search && !queryParams.name && !queryParams.asset_id) {
      queryParams.name = params.search;
    }
    return companyLookupAPI.getDescriptors(queryParams);
  },
  
  /**
   * Get endpoints by domain (legacy - use companyEndpointsAPI instead)
   * @param {string} domain - Company domain
   * @returns {Promise} Infrastructure endpoints for the domain
   */
  getEndpointsByDomain: (domain) => companyLookupAPI.getByDomain(domain)
};

// ============================================================================
// COMPANY LOOKUP API - MX Discovery
// ============================================================================

export const companyLookupAPI = {
  /**
   * Company Lookup - MX Discovery (single /companies endpoint)
   * Unified search that accepts name or asset_id
   * Returns companies array: { "result": [...], "paging_metadata": {...} }
   * 
   * @param {Object} params - Search parameters
   * @param {string} params.name - Company name (Option 1)
   * @param {string} params.asset_id - Asset ID (Option 2)
   * @returns {Promise} Response with companies array
   */
  lookup: ({ name, asset_id, cursor, limit } = {}) => {
    const params = buildCompaniesQueryParams({ name, asset_id, cursor, limit });
    return api.get('/api/v2/companies', { params });
  },
  
  /**
   * Get all companies (no search)
   * @returns {Promise} All companies
   */
  getAll: () => api.get('/api/v2/companies'),
  
  /**
   * Get all company descriptors with full details
   * @param {Object} [params] - Optional query parameters
   * @param {string} [params.name] - Filter by company name
   * @param {string} [params.asset_id] - Filter by asset id
   * @returns {Promise} Response with full company descriptors
   */
  getDescriptors: (params = {}) =>
    api.get('/api/v2/companies', { params: buildCompaniesQueryParams(params) }),
  
  /**
   * Add a new company descriptor
   * @param {Object} descriptor - Canonical companies payload
   * @returns {Promise} Response with added descriptor
   */
  add: (descriptor) => api.post('/api/v2/companies', descriptor),

  /**
   * Retrieve one company by domain using Base64URL id path.
   * @param {string} domain - Company domain (plain string)
   * @returns {Promise} Response with company payload
   */
  getByDomain: (domain) => api.get(`/api/v2/companies/${toBase64Url(domain)}`),

  /**
   * Retrieve one company by base64url-encoded domain id path value.
   * @param {string} companyId - Path id value
   * @returns {Promise} Response with company payload
   */
  getById: (companyId) => api.get(`/api/v2/companies/${companyId}`),

  /**
   * Update one company by domain using Base64URL id path.
   * @param {string} domain - Company domain
   * @param {Object} descriptor - Canonical company payload
   * @returns {Promise} Response with updated payload
   */
  updateByDomain: (domain, descriptor) =>
    api.put(`/api/v2/companies/${toBase64Url(domain)}`, descriptor),

  /**
   * Update one company by id path.
   * @param {string} companyId - Path id value
   * @param {Object} descriptor - Canonical company payload
   * @returns {Promise} Response with updated payload
   */
  updateById: (companyId, descriptor) => api.put(`/api/v2/companies/${companyId}`, descriptor),

  /**
   * Delete one company by domain using Base64URL id path.
   * @param {string} domain - Company domain
   * @returns {Promise} Empty response (204)
   */
  deleteByDomain: (domain) => api.delete(`/api/v2/companies/${toBase64Url(domain)}`),

  /**
   * Delete one company by id path.
   * @param {string} companyId - Path id value
   * @returns {Promise} Empty response (204)
   */
  deleteById: (companyId) => api.delete(`/api/v2/companies/${companyId}`)
};

export const companyEndpointsAPI = {
  /**
   * Legacy compatibility wrapper via /companies
   * @param {string} domain - Optional domain filter
   * @returns {Promise} Company lookup response
   */
  getEndpoints: (domain = null) => {
    if (domain) {
      return companyLookupAPI.getByDomain(domain);
    }
    return api.get('/api/v2/companies');
  }
};

// Registry API - Legacy for backward compatibility
export const registryAPI = {
  // Use smart search that automatically detects asset IDs, serial numbers, etc.
  getDescriptors: (params = {}) => {
    // If search query provided, use smart search to detect type (asset ID, serial number, etc.)
    if (params.search) {
      // Extract pagination params
      const paginationParams = {
        page: params.page,
        per_page: params.per_page
      };
      // Use smart search which will detect asset IDs and route correctly
      return smartSearch(params.search, paginationParams);
    }
    // Otherwise use direct query
    return dataProvidersAPI.query(params);
  },
  
  // Legacy endpoints
  getDescriptor: (id) => api.get(`/api/registry-descriptors/${id}`),
  getRegistryAccess: (id) => api.get(`/api/registry-access/${id}`),
  proxyRegistry: (id) => api.get(`/api/proxy-registry/${id}`),
  getLocalContent: (id) => api.get(`/api/local-registry-content/${id}`),
  forwardRequest: (url) => api.get('/api/forward-request', { params: { url } }),
  
  // ============================================================================
  // NEW SPEC API ENDPOINTS (DQL & Canonical Data Shapes)
  // ============================================================================
  
  // Yellow Pages - Company discovery
  /**
   * Query companies using DQL (Discovery Query Language)
   * @param {string} dqlQuery - DQL query string, e.g., "FROM Company WHERE legalName ^= \"ACME\" OR \"motion\" in tags"
   * @returns {Promise} Response with companies matching the query
   */
  queryCompanies: (dqlQuery) => api.get('/api/v2/yellow-pages/companies/query', { 
    params: { q: dqlQuery } 
  }),
  
  /**
   * Get company by companyId, assetId, or domain
   * @param {string} companyId - Company ID (urn:uuid:...), assetId (urn:aas:company:...), or domain
   * @returns {Promise} Company details
   */
  getCompany: (companyId) => api.get(`/api/v2/yellow-pages/companies/${companyId}`),
  
  // Yellow Pages - Capability discovery
  /**
   * Query capabilities using DQL
   * @param {string} dqlQuery - DQL query string, e.g., "FROM Capability WHERE category = \"motion\" AND \"pmsm\" in tags"
   * @returns {Promise} Response with capabilities matching the query
   */
  queryCapabilities: (dqlQuery) => api.get('/api/v2/yellow-pages/capabilities/query', { 
    params: { q: dqlQuery } 
  }),
  
  // Address Book - Holder Inventory
  /**
   * Get HolderInventory by holderCompanyId
   * @param {string} holderId - Holder company ID (urn:uuid:...)
   * @returns {Promise} HolderInventory with all asset pointers
   */
  getHolderInventory: (holderId) => api.get(`/api/v2/address-book/holder-inventory/${holderId}`),
  
  /**
   * Query assets in HolderInventory using DQL
   * @param {string} holderId - Holder company ID
   * @param {string} dqlQuery - DQL query string, e.g., "FROM HolderInventory WHERE assetId = \"urn:iec:61406:acme:PN12345:SN98765\""
   * @returns {Promise} Response with matching asset pointers
   */
  queryHolderInventory: (holderId, dqlQuery) => api.get(`/api/v2/address-book/holder-inventory/${holderId}/query`, { 
    params: { q: dqlQuery } 
  }),
  
  // Address Book - Asset discovery
  /**
   * Find all holders that have a specific asset in their inventory
   * @param {string} assetId - Asset ID (e.g., urn:iec:61406:acme:PN12345:SN98765)
   * @returns {Promise} List of holders with this asset
   */
  getAssetHolders: (assetId) => api.get(`/api/v2/address-book/assets/${assetId}/holders`),
  
  /**
   * Get AccessLink for an asset (credential-free pointer bundle)
   * @param {string} assetId - Asset ID
   * @returns {Promise} AccessLink with links to AAS_REGISTRY, AAS_SERVER, HOLDER_INVENTORY
   */
  getAccessLink: (assetId) => api.get(`/api/v2/address-book/access-links/${assetId}`),
  
  // ============================================================================
  // LEGACY ENDPOINTS (deprecated - use DQL endpoints above)
  // ============================================================================
  searchAssets: (filters = {}) => api.get('/api/v2/address-book/assets', { params: filters }),
  getAsset: (assetId) => api.get(`/api/v2/address-book/assets/${assetId}`),
  getEndpoints: (domain) => api.get(`/api/v2/address-book/endpoints/${domain}`),
  searchCompanies: (filters = {}) => api.get('/api/v2/yellow-pages/companies', { params: filters }),
  
  // Simplified registry call - no JWT tokens needed
  callRegistryDirect: async (registryUrl, path, token, method = 'GET', data = null) => {
    const config = {
      method,
      url: `${registryUrl}${path}`,
      headers: {
        'Content-Type': 'application/json'
      }
    };
    
    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.data = data;
    }
    
    return axios(config);
  }
};

// AASX API
export const aasxAPI = {
  getDescriptors: () => api.get('/api/aasx/descriptors'),
  getDescriptor: (id) => api.get(`/api/aasx/descriptors/${id}`),
  getServers: (id) => api.get(`/api/aasx/descriptors/${id}/servers`),
  getCategorization: (id) => api.get(`/api/aasx/descriptors/${id}/categorization`),
};

// Onboarding API
export const onboardAPI = {
  submitOrganization: (data) => api.post('/api/onboard', data),
};

// ============================================================================
// SMART SEARCH - Automatically routes to correct data provider query
// ============================================================================

export const smartSearch = (query, paginationParams = {}) => {
  /**
   * Smart search that automatically detects search type and routes to unified endpoint
   * @param {string} query - Search query
   * @param {Object} paginationParams - Pagination parameters (page, per_page)
   * @returns {Promise} Data providers matching the query
   */
  
  const params = { ...paginationParams };
  
  if (!query || !query.trim()) {
    // Empty query returns all providers
    return dataProvidersAPI.query(params);
  }
  
  const trimmedQuery = query.trim();
  
  // Check if it's a URL or contains URL patterns (Asset ID)
  // Enhanced detection for asset ID URLs like:
  // - https://admin-shell.io/asset/123
  // - https://admin-shell.io/idta/asset/123
  // - http://example.com/asset/123
  const isUrlPattern = /^https?:\/\//i.test(trimmedQuery);
  const hasUrlStructure = trimmedQuery.includes('://') && 
    (trimmedQuery.match(/\.(com|io|org|net|de)(\/|$)/i) || 
     trimmedQuery.includes('/asset/') || 
     trimmedQuery.includes('/idta/') ||
     trimmedQuery.includes('/ids/asset/'));
  
  if (isUrlPattern || hasUrlStructure) {
    // This is definitely an asset ID search
    if (process.env.NODE_ENV === 'development') {
      console.log('Detected asset ID URL:', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Check if query contains asset ID keywords (admin shell io, asset, ids/asset, etc.)
  // This handles searches like "admin shell io asset" or "phoenixcontact asset"
  const queryLower = trimmedQuery.toLowerCase();
  const assetIdKeywords = ['asset', 'ids/asset', '/asset/', 'asset/', 'asset id', 'assetid'];
  const hasAssetKeyword = assetIdKeywords.some(keyword => queryLower.includes(keyword));
  
  // Check for domain patterns (with or without spaces/hyphens)
  // "admin shell io" -> "admin-shell.io" or "admin.shell.io"
  // Also check for partial matches like "admin shell" or "shell io"
  const domainPatterns = [
    /admin[\s\-_]*shell[\s\-_]*io/i,     // "admin shell io", "admin-shell-io", "admin_shell_io"
    /admin[\s\-_]*shell/i,                // "admin shell" (partial)
    /shell[\s\-_]*io/i,                   // "shell io" (partial)
    /phoenix[\s\-_]*contact/i,            // "phoenix contact", "phoenix-contact"
    /lenze/i,                             // "lenze"
    /siemens/i,                           // "siemens"
    /[a-z0-9-]+\.(io|com|org|net|de)(\/|$)/i   // Any domain pattern like "example.com" or "example.com/asset"
  ];
  const hasDomainPattern = domainPatterns.some(pattern => pattern.test(trimmedQuery));
  
  // Also check if query contains domain-like words (admin, shell, io together)
  const hasDomainKeywords = (queryLower.includes('admin') && queryLower.includes('shell') && queryLower.includes('io')) ||
                           (queryLower.includes('phoenix') && queryLower.includes('contact')) ||
                           queryLower.includes('lenze') ||
                           queryLower.includes('siemens');
  
  // Debug logging (can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Smart Search Detection:', {
      query: trimmedQuery,
      hasAssetKeyword,
      hasDomainPattern,
      hasDomainKeywords,
      queryLower
    });
  }
  
  // If query contains "asset" keyword AND domain/company name, it's likely an asset ID search
  // OR if it contains domain pattern with TLD indicators
  if (hasAssetKeyword && (hasDomainPattern || hasDomainKeywords || queryLower.match(/\b(io|com|org|net|de)\b/i))) {
    // This looks like an asset ID pattern search - use asset_id parameter
    // The backend will match against asset_id_pattern fields
    if (process.env.NODE_ENV === 'development') {
      console.log('Routing to asset_id search:', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Also check if it's just a domain pattern (even without "asset" keyword)
  // This handles queries like "admin shell io" that should match asset ID patterns
  if ((hasDomainPattern || hasDomainKeywords) && 
      (queryLower.match(/\b(io|com|org|net|de)\b/i) || queryLower.match(/admin[\s-]*shell/i))) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Routing to asset_id search (domain pattern):', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Check if it looks like a serial number (contains common patterns like LEN-, PHX-, IDTA-)
  if (trimmedQuery.match(/^[A-Z]{2,5}[-_]/i) || 
      trimmedQuery.match(/^(SN|SERIAL|S\/N)[-_]?/i) || 
      trimmedQuery.match(/[-_][0-9]{4,}/)) {
    return dataProvidersAPI.query({ ...params, serial_number: trimmedQuery });
  }
  
  // Check if it looks like a specific domain
  if (trimmedQuery.match(/^[a-z0-9-]+\.(com|io|org|net|de)$/i)) {
    return companyLookupAPI.getByDomain(trimmedQuery).then((response) => ({
      ...response,
      data: {
        result: response?.data?.result ? [response.data.result] : [],
        paging_metadata: {}
      }
    }));
  }
  
  // Check if it looks like a submodel name (PascalCase)
  if (trimmedQuery.match(/^[A-Z][a-z]+([A-Z][a-z]+)+$/) && 
      (trimmedQuery.includes('Nameplate') || trimmedQuery.includes('Data') || 
       trimmedQuery.includes('Documentation') || trimmedQuery.includes('Information'))) {
    return dataProvidersAPI.query({ ...params, submodel: trimmedQuery });
  }
  
  // Otherwise, use universal search (searches all fields)
  return dataProvidersAPI.query({ ...params, search: trimmedQuery });
};

// Legacy mapping API - redirects to unified endpoint
export const mappingAPI = {
  byAssetId: (assetId) => dataProvidersAPI.query({ asset_id: assetId }),
  bySerialNumber: (serialNumber) => dataProvidersAPI.query({ serial_number: serialNumber }),
  byAasId: (aasId) => dataProvidersAPI.query({ aas_id: aasId }),
  byCompanyName: (companyName) => dataProvidersAPI.query({ company_name: companyName }),
  bySubmodel: (submodel) => dataProvidersAPI.query({ submodel }),
  byCapability: (capability) => dataProvidersAPI.query({ capability }),
  byProductGroup: (productGroup) => dataProvidersAPI.query({ product_group: productGroup }),
  smartSearch: smartSearch
};

// System API
export const systemAPI = {
  performance: () => api.get('/api/performance'),
};

export default api;
